import { Vector3 } from 'three';
import { RackElement } from '@thive/canvas';
import { ConstellationSpec, LoadType, SingularStationArea } from '@thive/fddm-types';

import { numberOfColumns } from '@/modules/common/helpers/loadCarrier';
import { elementFactory } from '@/modules/canvas/factories';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';

const getElevationRackLength = (
  distribution: ConstellationSpec['distribution'],
  gap: number,
  boxLength: number,
  loadLength: number,
  numberOfLoads: number,
) => (distribution === 'GAP' ? boxLength : (loadLength + gap) * numberOfLoads);

export const createElevationRack = (
  area: SingularStationArea,
  loadTypes: LoadType[],
): RackElement | null => {
  const { rectangle, constellationSpec, stationSpec } = area;
  const { stacking, spacing, distribution } = constellationSpec;
  const gap = spacing.minGap;
  const margin = spacing.minMargin;
  const { z } = stationSpec;

  const isHorizontal =
    stacking === 'HORIZONTAL_TOP' ||
    stacking === 'HORIZONTAL_MIDDLE' ||
    stacking === 'HORIZONTAL_BOTTOM';

  // rack dimensions
  const boxLength = isHorizontal ? rectangle.length : rectangle.width;
  const loadType = loadTypes[0];
  if (!loadType) return null;

  const loadLength = loadType.length;
  const rackDepth = loadType.width;
  const numberOfLoads = numberOfColumns(boxLength, gap, margin, loadLength);
  if (numberOfLoads <= 0) return null;

  const rackLength = getElevationRackLength(
    distribution,
    gap,
    boxLength,
    loadLength,
    numberOfLoads,
  );

  // position & rotation are relative to the rect from createArea() above
  const position = new Vector3(0, 0, 0);
  const rotation = new Vector3(0, 0, 0);

  if (isHorizontal) {
    let scale: number;
    if (stacking === 'HORIZONTAL_TOP') {
      scale = 1;
    } else if (stacking === 'HORIZONTAL_MIDDLE') {
      scale = 0.5;
    } else {
      scale = 0;
    }

    const xOffset = -rectangle.length / 2 + 0.5 * (rectangle.length - rackLength); // center in X
    const yOffset = -rectangle.width / 2 + scale * (rectangle.width - rackDepth);

    position.add(new Vector3(xOffset, yOffset, 0));
  } else {
    let scale: number;
    if (stacking === 'VERTICAL_LEFT') {
      scale = 0;
    } else if (stacking === 'VERTICAL_MIDDLE') {
      scale = 0.5;
    } else {
      scale = 1;
    }
    const xOffset = scale * (rectangle.width - rackDepth) + rackDepth;
    const yOffset = 0.5 * (boxLength - rackLength); // center in Y

    position.add(new Vector3(xOffset, yOffset, 0));
    rotation.z += 90;
  }

  return elementFactory.buildRackElement({
    id: `${area.id}-elevation`,
    name: `${area.name}-elevation`,
    position,
    rotation,
    tags: [FDDM_TAG.MODEL],
    frameLength: rackLength,
    frameDepth: rackDepth,
    shelfHeights: [z],
    shelfLength: rackLength / numberOfLoads,
    parentId: `${area.id}-3d-rect`, // attach to 3D singular station rect for rotation
  });
};
